import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { showSuccess, showError } from "@/lib/toastHelpers";

type BlastJob = {
  _id: string;
  type: string;
  refName: string;
  subject: string;
  status: "queued" | "running" | "completed" | "failed";
  totalCount: number;
  sentCount: number;
  failedCount: number;
  suppressedCount: number;
  deadLetterCount: number;
  openedCount: number;
  scheduledAt: string;
  createdAt: string;
  completedAt?: string;
};

const STATUS_COLORS: Record<string, string> = {
  queued:    "bg-yellow-100 text-yellow-800",
  running:   "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  failed:    "bg-red-100 text-red-800",
};

export default function QueueTab() {
  const [selectedJob, setSelectedJob] = useState<BlastJob | null>(null);
  const [recipientPage, setRecipientPage] = useState(1);
  const [retrying, setRetrying] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data: jobs = [], isLoading } = useQuery<BlastJob[]>({
    queryKey: ["/api/admin/email-queue"],
    queryFn: () => apiRequest("GET", "/api/admin/email-queue") as unknown as Promise<BlastJob[]>,
    refetchInterval: 10_000,
  });

  const { data: detail } = useQuery({
    queryKey: ["/api/admin/email-queue", selectedJob?._id, recipientPage],
    queryFn: () => apiRequest("GET", `/api/admin/email-queue/${selectedJob!._id}?page=${recipientPage}&limit=20`) as unknown as Promise<any>,
    enabled: !!selectedJob,
  });

  async function retryJob(jobId: string) {
    setRetrying(jobId);
    try {
      const result: any = await apiRequest("POST", `/api/admin/email-queue/${jobId}/retry`);
      showSuccess(`Re-queued ${result.requeued} failed recipients`);
      qc.invalidateQueries({ queryKey: ["/api/admin/email-queue"] });
      if (selectedJob?._id === jobId) {
        qc.invalidateQueries({ queryKey: ["/api/admin/email-queue", jobId] });
      }
    } catch (e: any) {
      showError(e.message || "Retry failed");
    } finally {
      setRetrying(null);
    }
  }

  if (isLoading) return <div className="text-muted-foreground text-sm">Loading queue...</div>;

  return (
    <div className="space-y-4">
      {jobs.length === 0 && (
        <p className="text-sm text-muted-foreground">No blast jobs yet.</p>
      )}

      <div className="space-y-2">
        {jobs.map(job => (
          <Card
            key={job._id}
            className={`cursor-pointer transition-colors hover:bg-muted/40 ${selectedJob?._id === job._id ? "ring-2 ring-primary/30" : ""}`}
            onClick={() => { setSelectedJob(j => j?._id === job._id ? null : job); setRecipientPage(1); }}
          >
            <CardContent className="py-3 px-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm truncate">{job.refName}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[job.status] || "bg-gray-100 text-gray-700"}`}>
                      {job.status}
                    </span>
                    <span className="text-xs text-muted-foreground capitalize">{job.type}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{job.subject}</p>
                  <div className="flex flex-wrap gap-3 text-xs mt-1.5">
                    <span className="text-blue-700">✉ {job.sentCount} sent</span>
                    {job.failedCount > 0 && <span className="text-red-600">✗ {job.failedCount} failed</span>}
                    {job.suppressedCount > 0 && <span className="text-gray-500">◌ {job.suppressedCount} suppressed</span>}
                    {(job.deadLetterCount || 0) > 0 && <span className="text-slate-500">☠ {job.deadLetterCount} dead-lettered</span>}
                    {job.openedCount > 0 && <span className="text-purple-600">👁 {job.openedCount} opened</span>}
                    <span className="text-muted-foreground">of {job.totalCount}</span>
                  </div>
                </div>
                <div className="text-right text-xs text-muted-foreground shrink-0 space-y-1">
                  <div>{new Date(job.scheduledAt).toLocaleString()}</div>
                  {job.failedCount > 0 && (job.status === "completed" || job.status === "failed") && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-6 px-2"
                      disabled={retrying === job._id}
                      onClick={e => { e.stopPropagation(); retryJob(job._id); }}
                    >
                      {retrying === job._id ? "Retrying…" : "Retry failed"}
                    </Button>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              {job.status === "running" && job.totalCount > 0 && (
                <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all"
                    style={{ width: `${Math.round(((job.sentCount + job.failedCount + job.suppressedCount + (job.deadLetterCount || 0)) / job.totalCount) * 100)}%` }}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recipient detail panel */}
      {selectedJob && detail && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Recipients — {selectedJob.refName}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {detail.recipients?.map((r: any) => (
                <div key={r._id} className="flex items-center justify-between text-xs py-1 border-b last:border-0">
                  <span className="truncate text-muted-foreground">{r.email}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.processedAt && <span className="text-muted-foreground/60">{new Date(r.processedAt).toLocaleTimeString()}</span>}
                    <Badge
                      variant="outline"
                      className={`text-xs ${r.status === "sent" ? "text-green-700 border-green-200" : r.status === "failed" ? "text-red-700 border-red-200" : r.status === "suppressed" ? "text-gray-500" : "text-yellow-700 border-yellow-200"}`}
                    >
                      {r.status}
                    </Badge>
                    {r.error && <span className="text-red-500 truncate max-w-[160px]" title={r.error}>{r.error}</span>}
                  </div>
                </div>
              ))}
            </div>

            {detail.total > 20 && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t">
                <Button size="sm" variant="outline" onClick={() => setRecipientPage(p => Math.max(1, p - 1))} disabled={recipientPage === 1}>← Prev</Button>
                <span className="text-xs text-muted-foreground">Page {recipientPage} of {Math.ceil(detail.total / 20)}</span>
                <Button size="sm" variant="outline" onClick={() => setRecipientPage(p => p + 1)} disabled={recipientPage >= Math.ceil(detail.total / 20)}>Next →</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
